import {countThresholdDays} from "../../helpers/countThresholdDays"
import {insertTravelDates} from "../../helpers/insertTravelDates"
import {sortByPercentOfTaxThreshold} from "../../helpers/sortByPercentOfTaxThreshold"
import {getGoogleTrips} from "../../modules"
import initialState from "./initialState"

// TYPES
export const UPDATE_PAYLOAD = "UPDATE_PAYLOAD"

// SELECTORS
export const getPayload = (state) => getGoogleTrips(state).payload

// 1
export const getPayloadItem = (state, id) =>
  getPayload(state).find((item) => item.id === id)

// 2
export const getTaxThresholds = (state, id) => {
  const payloadItem = getPayloadItem(state, id) || {}
  // Sort data in decreasing order of tax threshold used
  const sortedData = sortByPercentOfTaxThreshold(
    payloadItem.currentDate,
    payloadItem.jurisdictions
  )
  // Filter data by threshold
  return sortedData.filter((jurisdiction) => {
    // Determine the number of threshold days based on jurisdiction
    const thresholdDays = countThresholdDays(
      payloadItem.currentDate,
      jurisdiction.jursidiction,
      jurisdiction.travelDates
    )
    // Return only the jurisdictions which have passed a threshold
    return thresholdDays / jurisdiction.daysAllowed > 0
  })
}

// CREATORS
export const updatePayload = (payload) => ({
  type: UPDATE_PAYLOAD,
  payload,
})

// THUNKS
export const updateTravelInfo = (scenarioId, targetDates, params) => (
  dispatch,
  getState
) => {
  const payload = getPayload(getState()) || {}
  // Map over scenarios
  const updatedPayload = payload.map((scenario) => {
    // Ignore scenarios not matching the target scenario ID
    if (scenario.id !== scenarioId) return scenario
    return {
      ...scenario,
      jurisdictions: scenario.jurisdictions.map((jurisdiction) => {
        // Map over the target user payload to update the params of target dates
        return {
          ...jurisdiction,
          travelDates: jurisdiction.travelDates.map((travelDate) => {
            // Ignore dates not included in the input array
            if (!targetDates.includes(travelDate.date)) return travelDate
            // For the target date, update the params
            return {...travelDate, ...params}
          }),
        }
      }),
    }
  })

  dispatch(updatePayload(updatedPayload))
}

export const updateTravelJurisdiction = (
  scenarioId,
  targetDates,
  targetJurisdiction
) => (dispatch, getState) => {
  // Get payload state
  const payload = getPayload(getState()) || {}

  // Prepare an array of travel date objects from the target dates array, with default params
  const targetDateObjects = targetDates.map((targetDate) => ({
    date: targetDate,
    type: "work",
    confirmed: false,
  }))

  const updatedPayload = payload.map((scenario) => {
    // Ignore scenarios not matching the target scenario ID
    if (scenario.id !== scenarioId) return scenario

    const payloadWithTargetDatesRemoved = scenario.jurisdictions.map(
      (jurisdiction) => {
        // Ignore scenarios not matching the target scenario ID
        if (scenario.id !== scenarioId) return jurisdiction
        // Update jurisdictions for the target scenario
        /** We need to filter the `travelDates` array to remove target dates, but we also need
         * to preserve the data for the dates as we move them to a new jurisdiction. */
        return {
          ...jurisdiction,
          travelDates: jurisdiction.travelDates.filter((travelDate) => {
            // Get the index of the corresponding target date object
            const targetIndex = targetDates.findIndex(
              (targetDate) => targetDate === travelDate.date
            )
            // Determine if the travel date is included in the target dates array
            const travelDateIsTarget = targetIndex > -1
            // If the travel date is a target
            if (travelDateIsTarget) {
              // Update the target date type
              targetDateObjects[targetIndex].type = travelDate.type
            }
            // Filter out travel dates which are targets
            return !travelDateIsTarget
          }),
        }
      }
    )

    // To determine if the jurisdiction exists in state
    let jurisdictionExists = false

    // Map over filtered payload to add target dates to the target jurisdiction
    const payloadForExistingJurisdiction = payloadWithTargetDatesRemoved.map(
      (jurisdiction) => {
        // If this jurisdiction is not the target, return it as is
        if (jurisdiction.jurisdiction !== targetJurisdiction)
          return jurisdiction
        // If this jurisdiction is the target, determine that the target jurisdiction exists
        jurisdictionExists = true
        // Update the jurisdiction travel dates with the target dates
        const updatedTravelDates = insertTravelDates(
          targetDateObjects,
          jurisdiction.travelDates
        )
        // Return the jurisdiction with updated travel dates
        return {...jurisdiction, travelDates: updatedTravelDates}
      }
    )

    // If the target jurisdiction exists
    if (jurisdictionExists) {
      // Return the user object with the updated payload
      return {...scenario, jurisdictions: payloadForExistingJurisdiction}
    }

    // If the jurisdiction does not exist, prepare a new one
    const newJurisdictionWithTargetDates = {
      jurisdiction: targetJurisdiction,
      daysAllowed: 30,
      travelDates: targetDateObjects,
    }

    // Return the user object with the new jurisdiction
    // TODO: Solve the mystery of why it's not possible to unshift the new jurisdiction
    return {
      ...scenario,
      jurisdictions: [
        ...payloadWithTargetDatesRemoved,
        newJurisdictionWithTargetDates,
      ],
    }
  })

  // Update payload state
  dispatch(updatePayload(updatedPayload))
}

// REDUCER
export default (state = initialState, action) => {
  switch (action.type) {
    case UPDATE_PAYLOAD:
      return action.payload
    default:
      return state
  }
}
